<template>
  <div class="detail-report-table">
    <div v-if="isFetching" class="loading_state">
      <spinner />
    </div>

    <empty-state
      v-else-if="!tableData.length"
      :title="$t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.NO_REPORT_DATA')"
    />

    <template v-else>
      <ve-table
        :fixed-header="true"
        :columns="columns"
        :max-height="'calc(100vh - 450px)'"
        :table-data="tableData"
        :border-around="true"
      />
      <div class="total-call-minutes-summary">
        <span class="total-label">{{
          $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.DETAIL_REPORT.TOTAL')
        }}</span>
        <span class="total-value">
          {{ totalCallMinutesSum.toLocaleString() }} minutes
        </span>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { VeTable } from 'vue-easytable';
import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';

export default {
  components: {
    VeTable,
    Spinner,
    EmptyState,
  },
  props: {
    detailReportData: {
      type: Object,
      default: null,
    },
    reportPeriod: {
      type: String,
      default: 'days',
    },
    isFetching: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      uiFlags: 'campaign_agents/getUIFlags',
    }),
    reportPeriods() {
      if (!this.detailReportData || !this.detailReportData.periods) {
        return [];
      }

      return this.detailReportData.periods.map(period => {
        const date = new Date(period.date);

        if (this.reportPeriod === 'days') {
          // Format: D/M (day/month)
          return {
            key: period.key,
            label: `${date.getDate()}/${date.getMonth() + 1}`,
          };
        }

        if (this.reportPeriod === 'weeks') {
          // Format: D/M (first day of week)
          return {
            key: period.key,
            label: `${date.getDate()}/${date.getMonth() + 1}`,
          };
        }

        if (this.reportPeriod === 'months') {
          // Format: Mon (D/M)
          return {
            key: period.key,
            label: `${date.toLocaleDateString('en-US', {
              month: 'short',
            })} (${date.getDate()}/${date.getMonth() + 1})`,
          };
        }
        return { key: period.key, label: period.key };
      });
    },
    columns() {
      const columnList = [
        {
          field: 'metric',
          key: 'metric',
          title: this.$t(
            'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.DETAIL_REPORT.RESULT'
          ),
          width: 200,
          align: 'left',
          fixed: 'left',
          renderBodyCell: ({ row }) => (
            <span class="metric-cell">{row.label}</span>
          ),
        },
      ];

      this.reportPeriods.forEach(period => {
        columnList.push({
          field: period.key,
          key: period.key,
          title: period.label,
          width: 100,
          align: 'center',
          renderBodyCell: ({ row }) => {
            const value = row[period.key] || '0';
            return (
              <span
                class={{
                  'data-cell': true,
                  'data-cell-bold': row.isBold,
                }}
              >
                {value}
              </span>
            );
          },
        });
      });

      return columnList;
    },
    tableData() {
      if (!this.detailReportData || !this.detailReportData.periods) {
        return [];
      }

      const periods = this.reportPeriods;
      const data = this.detailReportData.data;

      return [
        {
          key: 'success',
          label: this.$t(
            'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.DETAIL_REPORT.SUCCESS'
          ),
          isBold: false,
          ...this.getRowData(periods, data, 'success'),
        },
        {
          key: 'reject',
          label: this.$t(
            'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.DETAIL_REPORT.REJECT'
          ),
          isBold: false,
          ...this.getRowData(periods, data, 'reject'),
        },
        {
          key: 'disqualified',
          label: this.$t(
            'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.DETAIL_REPORT.DISQUALIFIED'
          ),
          isBold: false,
          ...this.getRowData(periods, data, 'disqualified'),
        },
        {
          key: 'no_pickup',
          label: this.$t(
            'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.DETAIL_REPORT.NO_PICKUP'
          ),
          isBold: false,
          ...this.getRowData(periods, data, 'no_pickup'),
        },
        {
          key: 'total_attempt',
          label: this.$t(
            'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.DETAIL_REPORT.TOTAL_CALL_ATTEMPT'
          ),
          isBold: false,
          ...this.getRowData(periods, data, 'total_attempt'),
        },
        {
          key: 'total_call_minute',
          label: this.$t(
            'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.DETAIL_REPORT.TOTAL_CALL_MINUTE'
          ),
          isBold: false,
          ...this.getRowData(periods, data, 'total_call_minute'),
        },
        {
          key: 'total_connected_minute',
          label: this.$t(
            'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.DETAIL_REPORT.TOTAL_CONNECTED_CALL_MINUTE'
          ),
          isBold: false,
          ...this.getRowData(periods, data, 'total_connected_minute'),
        },
      ];
    },
    totalCallMinutesSum() {
      if (!this.detailReportData || !this.detailReportData.periods) {
        return 0;
      }
      const periods = this.reportPeriods;
      const data = this.detailReportData.data;
      let totalSum = 0;
      periods.forEach(period => {
        const periodData = data[period.key] || {};
        totalSum += periodData.total_call_minute || 0;
      });
      return totalSum;
    },
  },
  methods: {
    getRowData(periods, data, metric) {
      const result = {};
      periods.forEach(period => {
        const periodData = data[period.key] || {};
        const value = periodData[metric] || 0;
        result[period.key] = value.toLocaleString();
      });
      return result;
    },
  },
};
</script>
<style lang="scss">
.detail-report-table {
  .ve-table {
    padding-bottom: 0 !important;
  }
}
</style>
<style scoped lang="scss">
@import '~dashboard/assets/scss/widgets/_ps_table.scss';

.loading_state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
}

// Detail report table cell styles
.metric-cell {
  font-weight: 500;
  color: var(--s-800);
}

.data-cell {
  color: var(--s-700);

  &.data-cell-bold {
    font-weight: 600;
    color: var(--s-900);
  }
}

.total-call-minutes-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background-color: var(--s-25);
  border-radius: var(--border-radius-normal);
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 16px;

  .total-label {
    color: var(--s-800);
  }

  .total-value {
    color: var(--s-900);
  }
}
</style>
